import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Loader2, Pencil, Plus, RotateCcw, Star, Trash2 } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgSettings, OrgSettingsUpdate } from '../../hooks/useOrgSettings';
import { usePlatformQuoteDefaults } from '../../hooks/usePlatformQuoteDefaults';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CalculateTaxesCard } from '../../components/ui/CalculateTaxesCard';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import {
  TravelTimeRate,
  QuoteNumberConfig,
  QuoteNumberDigitCount,
  QuoteNumberMethod,
  QuoteNumberSeparator,
  ItemizationPreference,
  QUOTE_NUMBER_CONFIG_DEFAULTS,
  Organization,
  ShiftSetting,
  DEFAULT_SHIFT_SETTINGS,
} from '../../types/admin';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';

const isDevMode = !IS_FIREBASE_CONFIGURED;

// ── Constants ──────────────────────────────────────────────────────────────

function travelRateLabel(rate: TravelTimeRate, fixedHours: number): string {
  if (rate === 'portal_to_portal') return 'Portal to Portal';
  return `${fixedHours}-hour Fixed`;
}

const VALIDITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'null', label: 'Never expires' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '45', label: '45 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: 'custom', label: 'Custom' },
];

const SEPARATOR_OPTIONS: { value: QuoteNumberSeparator; label: string }[] = [
  { value: '-', label: 'Hyphen (-)' },
  { value: '.', label: 'Dot (.)' },
  { value: ' ', label: 'Space' },
  { value: '', label: 'None' },
];

const SEPARATOR_LABELS: Record<QuoteNumberSeparator, string> = {
  '-': 'Hyphen (-)',
  '.': 'Dot (.)',
  ' ': 'Space',
  '': 'None',
};

const DIGIT_COUNT_OPTIONS: { value: string; label: string }[] = [
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
];

const METHOD_OPTIONS: { value: QuoteNumberMethod; label: string }[] = [
  { value: 'sequential', label: 'Sequential' },
  { value: 'year-sequential', label: 'Year-Sequential' },
  { value: 'random', label: 'Random' },
  { value: 'date-based', label: 'Date-Based' },
];

const METHOD_LABELS: Record<QuoteNumberMethod, string> = {
  sequential: 'Sequential',
  'year-sequential': 'Year-Sequential',
  random: 'Random (6-digit)',
  'date-based': 'Date-Based',
};

const ITEMIZATION_OPTIONS: { value: ItemizationPreference; label: string; description: string }[] = [
  { value: 'single_line', label: 'Single Line Item', description: 'One combined charge for the entire move' },
  { value: 'crew_trucks', label: 'Itemize Crew & Trucks', description: 'Separate line items for each crew resource' },
  { value: 'duration_transit', label: 'Itemize Duration & Transit', description: 'Separate line items for job time, travel, and transit' },
];

const FLAT_RATE_ITEMIZATION_OPTIONS: { value: ItemizationPreference; label: string; description: string }[] = [
  { value: 'single_line', label: 'Single Line Item', description: 'One combined charge for the entire move' },
  { value: 'flat_rate_addons', label: 'Itemize Base Price + Add-ons', description: 'Base price as one line item, plus separate line items for each add-on (long carry, elevator, flights of stairs)' },
];

// ── Helpers ────────────────────────────────────────────────────────────────

function formatValidityDays(days: number | null | undefined): string {
  if (days === null || days === undefined) return 'Never expires';
  return `${days} days`;
}

function quoteNumberPreview(cfg: QuoteNumberConfig): string {
  if (cfg.method === 'random') {
    const rand = '847291';
    return cfg.prefix ? `${cfg.prefix}${cfg.separator}${rand}` : rand;
  }
  if (cfg.method === 'date-based') {
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return cfg.prefix
      ? `${cfg.prefix}${cfg.separator}${dateStr}${cfg.separator}01`
      : `${dateStr}${cfg.separator}01`;
  }
  const num = String(cfg.startingNumber).padStart(cfg.digitCount, '0');
  if (cfg.method === 'year-sequential') {
    const year = String(new Date().getFullYear());
    const parts: string[] = [];
    if (cfg.prefix) parts.push(cfg.prefix);
    parts.push(year);
    parts.push(num);
    return parts.join(cfg.separator);
  }
  return cfg.prefix ? `${cfg.prefix}${cfg.separator}${num}` : num;
}

/** Resolve move-type-specific travel time rate from org, falling back to generic. */
function getOrgTravelRate(org: Organization, type: 'commercial' | 'residential'): TravelTimeRate {
  if (type === 'commercial') {
    return org.commercialDefaultTravelTimeRate ?? org.defaultTravelTimeRate ?? 'one_hour_standard';
  }
  return org.residentialDefaultTravelTimeRate ?? org.defaultTravelTimeRate ?? 'one_hour_standard';
}

function getOrgFixedHours(org: Organization, type: 'commercial' | 'residential'): number {
  if (type === 'commercial') {
    return org.commercialFixedTravelTimeHours ?? org.fixedTravelTimeHours ?? 1;
  }
  return org.residentialFixedTravelTimeHours ?? org.fixedTravelTimeHours ?? 1;
}

function getOrgEfficiency(org: Organization, type: 'commercial' | 'residential'): number {
  if (type === 'commercial') {
    return org.commercialEfficiencyMultiplier ?? org.efficiencyMultiplier ?? 1;
  }
  return org.residentialEfficiencyMultiplier ?? org.efficiencyMultiplier ?? 1;
}

function getOrgValidityDays(org: Organization, type: 'commercial' | 'residential'): number | null | undefined {
  if (type === 'commercial') {
    return org.commercialDefaultQuoteValidityDays !== undefined
      ? org.commercialDefaultQuoteValidityDays
      : org.defaultQuoteValidityDays;
  }
  return org.residentialDefaultQuoteValidityDays !== undefined
    ? org.residentialDefaultQuoteValidityDays
    : org.defaultQuoteValidityDays;
}

// ── Shared sub-components ──────────────────────────────────────────────────

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex rounded-lg border border-stone bg-mist p-0.5 gap-0.5">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 h-9 rounded-md text-[13px] font-semibold transition-all duration-150 ${
            value === opt.value
              ? 'bg-white text-midnight shadow-sm'
              : 'text-slate hover:text-midnight'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function CardEditButton({ editing, onEdit, onSave, onCancel, saving }: {
  editing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
}) {
  if (!editing) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-plum border border-plum rounded-lg hover:bg-plum/5 transition-colors"
      >
        <Pencil size={12} />
        Edit
      </button>
    );
  }
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-plum text-white rounded-lg hover:bg-plum/90 disabled:opacity-50 transition-colors"
      >
        {saving && <Loader2 size={12} className="animate-spin" />}
        Save
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="px-3 py-1.5 text-xs font-medium text-slate border border-stone rounded-lg hover:bg-mist transition-colors"
      >
        Cancel
      </button>
    </div>
  );
}

export function InfoRow({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-stone last:border-b-0">
      <div className="flex-1 min-w-0">
        <span className="text-[13px] text-slate">{label}</span>
        {subtitle && (
          <p className="text-[11px] text-slate/70 leading-snug mt-0.5">{subtitle}</p>
        )}
      </div>
      <span className="text-[13px] font-semibold text-midnight shrink-0 ml-4 self-center">{value}</span>
    </div>
  );
}

function RestoreButton({ onRestore, label }: { onRestore: () => Promise<void>; label: string }) {
  const [showModal, setShowModal] = useState(false);

  async function handleConfirm() {
    await onRestore();
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate hover:text-plum transition-colors"
        title="Restore from platform defaults"
      >
        <RotateCcw size={11} />
        <span className="hidden sm:inline">Restore</span>
      </button>
      <ConfirmModal
        isOpen={showModal}
        title="Restore defaults"
        message={`This will override your current ${label} with the platform defaults. This action cannot be undone.`}
        confirmLabel="Restore"
        variant="danger"
        onConfirm={handleConfirm}
        onClose={() => setShowModal(false)}
      />
    </>
  );
}

// ── Quote Building Card (per move type) ───────────────────────────────────

/** Plain-language explainers the setup wizard shows under each Quote Building setting. */
export interface QuoteBuildingHints {
  travelTime?: string;
  localRadius?: string;
  efficiency?: string;
  validity?: string;
}

function SettingHint({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <details className="mt-1 group">
      <summary className="list-none cursor-pointer text-[12px] font-medium text-plum hover:text-rose select-none">
        What's this?
      </summary>
      <p className="text-[12px] text-slate leading-snug mt-1">{text}</p>
    </details>
  );
}

export function QuoteBuildingCard({ type, org, onSave, onRestore, onEditingChange, hints }: {
  type: 'commercial' | 'residential';
  org: Organization;
  onSave: (data: OrgSettingsUpdate) => Promise<void>;
  onRestore?: () => Promise<void>;
  /** Fires when the card enters or leaves edit mode (the setup wizard warns before discarding an open edit). */
  onEditingChange?: (editing: boolean) => void;
  /** "What's this?" disclosures under each setting (setup wizard). */
  hints?: QuoteBuildingHints;
}) {
  const [editing, setEditingState] = useState(false);
  const setEditing = (next: boolean) => { setEditingState(next); onEditingChange?.(next); };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const savedRate = getOrgTravelRate(org, type);
  const savedFixedHours = getOrgFixedHours(org, type);
  const savedEfficiency = getOrgEfficiency(org, type);
  const savedValidityDays = getOrgValidityDays(org, type);
  const savedRadiusType = org.localMoveRadiusType ?? 'distance';
  const savedRadiusValue = org.localMoveRadiusValue ?? 50;

  const [travelRate, setTravelRate] = useState<TravelTimeRate>(savedRate);
  const [fixedHours, setFixedHours] = useState(String(savedFixedHours));
  const [efficiency, setEfficiency] = useState(String(savedEfficiency));
  const [radiusType, setRadiusType] = useState<'distance' | 'time'>(savedRadiusType);
  const [radiusValue, setRadiusValue] = useState(String(savedRadiusValue));

  const initialSelection = savedValidityDays === null || savedValidityDays === undefined
    ? 'null'
    : [7, 14, 30, 45, 60, 90].includes(savedValidityDays) ? String(savedValidityDays) : 'custom';
  const [validitySelection, setValiditySelection] = useState(initialSelection);
  const [customValidityDays, setCustomValidityDays] = useState(
    initialSelection === 'custom' ? String(savedValidityDays) : '',
  );

  function startEdit() {
    const rate = getOrgTravelRate(org, type);
    const hours = getOrgFixedHours(org, type);
    const eff = getOrgEfficiency(org, type);
    const days = getOrgValidityDays(org, type);
    setTravelRate(rate);
    setFixedHours(String(hours));
    setEfficiency(String(eff));
    const sel = days === null || days === undefined
      ? 'null'
      : [7, 14, 30, 45, 60, 90].includes(days) ? String(days) : 'custom';
    setValiditySelection(sel);
    setCustomValidityDays(sel === 'custom' ? String(days) : '');
    setRadiusType(org.localMoveRadiusType ?? 'distance');
    setRadiusValue(String(org.localMoveRadiusValue ?? 50));
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      let validityDays: number | null = null;
      if (validitySelection !== 'null') {
        validityDays = validitySelection === 'custom'
          ? parseInt(customValidityDays, 10) || null
          : parseInt(validitySelection, 10);
      }
      const parsedHours = parseFloat(fixedHours) || 1;
      const parsedRadius = parseFloat(radiusValue) || (radiusType === 'distance' ? 50 : 1);
      const data: OrgSettingsUpdate = {
        ...(type === 'commercial'
          ? {
              commercialDefaultTravelTimeRate: travelRate,
              commercialFixedTravelTimeHours: parsedHours,
              commercialEfficiencyMultiplier: parseFloat(efficiency) || 1,
              commercialDefaultQuoteValidityDays: validityDays,
            }
          : {
              residentialDefaultTravelTimeRate: travelRate,
              residentialFixedTravelTimeHours: parsedHours,
              residentialEfficiencyMultiplier: parseFloat(efficiency) || 1,
              residentialDefaultQuoteValidityDays: validityDays,
            }),
        localMoveRadiusType: radiusType,
        localMoveRadiusValue: parsedRadius,
      };
      await onSave(data);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const effectiveFixedHours = parseFloat(fixedHours) || 1;

  const travelRateOptions: { value: TravelTimeRate; label: string }[] = [
    { value: 'one_hour_standard', label: editing ? `${effectiveFixedHours}-hour Fixed` : travelRateLabel('one_hour_standard', savedFixedHours) },
    { value: 'portal_to_portal', label: 'Portal to Portal' },
  ];

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Quote Building</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            These defaults are used when estimators build new {type} quotes. They can be adjusted on individual quotes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onRestore && !editing && <RestoreButton onRestore={onRestore} label="quote building settings" />}
          <CardEditButton editing={editing} onEdit={startEdit} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
        </div>
      </div>

      <div className="px-4">
        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {editing ? (
          <div className="py-4 flex flex-col gap-4">
            {/* Travel Time Rate */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                Travel Time Rate
              </p>
              <SegmentedControl options={travelRateOptions} value={travelRate} onChange={setTravelRate} />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                How travel time is calculated and billed for moves
              </p>
              <SettingHint text={hints?.travelTime} />
            </div>

            {/* Fixed Hours input — only when Fixed is selected */}
            {travelRate === 'one_hour_standard' && (
              <div>
                <label
                  htmlFor={`qd-fixed-hours-${type}`}
                  className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
                >
                  Fixed Travel Time Hours
                </label>
                <input
                  id={`qd-fixed-hours-${type}`}
                  type="number"
                  inputMode="decimal"
                  step="0.25"
                  min="0.25"
                  value={fixedHours}
                  onChange={e => setFixedHours(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
                <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                  The fixed number of hours to charge for travel time to the job site
                </p>
              </div>
            )}

            {/* Local Move Radius */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                Local Move Radius
              </p>
              <SegmentedControl
                options={[
                  { value: 'distance' as const, label: 'Distance (miles)' },
                  { value: 'time' as const, label: 'Time (hours)' },
                ]}
                value={radiusType}
                onChange={setRadiusType}
              />
              <div className="mt-2">
                <input
                  id={`qd-radius-${type}`}
                  type="number"
                  inputMode="decimal"
                  step={radiusType === 'distance' ? '1' : '0.5'}
                  min="0.1"
                  value={radiusValue}
                  onChange={e => setRadiusValue(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                {radiusType === 'distance'
                  ? 'Moves within this distance are classified as "Local"'
                  : 'Moves within this transit time are classified as "Local"'}
              </p>
              <SettingHint text={hints?.localRadius} />
            </div>

            {/* Efficiency Multiplier */}
            <div>
              <label
                htmlFor={`qd-efficiency-${type}`}
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
              >
                Efficiency Multiplier
              </label>
              <input
                id={`qd-efficiency-${type}`}
                type="number"
                inputMode="decimal"
                step="any"
                min="0.01"
                value={efficiency}
                onChange={e => setEfficiency(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Adjusts calculated job durations. Values above 1 increase durations, below 1 decrease them.
              </p>
              <SettingHint text={hints?.efficiency} />
            </div>

            {/* Default Quote Expiration */}
            <div>
              <label
                htmlFor={`qd-validity-${type}`}
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
              >
                Default Quote Expiration
              </label>
              <CustomDropdown
                id={`qd-validity-${type}`}
                options={VALIDITY_OPTIONS}
                value={validitySelection}
                onChange={setValiditySelection}
                openUpward
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                How long quotes remain valid. Can be overridden per quote.
              </p>
              <SettingHint text={hints?.validity} />
            </div>

            {validitySelection === 'custom' && (
              <div>
                <label
                  htmlFor={`qd-validity-custom-${type}`}
                  className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
                >
                  Custom Days
                </label>
                <input
                  id={`qd-validity-custom-${type}`}
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max="365"
                  value={customValidityDays}
                  onChange={e => setCustomValidityDays(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>
            )}

          </div>
        ) : (
          <>
            <InfoRow
              label="Travel Time Rate"
              value={travelRateLabel(savedRate, savedFixedHours)}
              subtitle="How travel time is calculated and billed for moves"
            />
            {hints?.travelTime && <div className="-mt-2 pb-3"><SettingHint text={hints.travelTime} /></div>}
            <InfoRow
              label="Local Move Radius"
              value={savedRadiusType === 'distance' ? `${savedRadiusValue} miles` : `${savedRadiusValue} hours`}
              subtitle="Moves within this radius are classified as Local"
            />
            {hints?.localRadius && <div className="-mt-2 pb-3"><SettingHint text={hints.localRadius} /></div>}
            <InfoRow
              label="Efficiency Multiplier"
              value={String(savedEfficiency)}
              subtitle="Adjusts calculated job durations. Values above 1 increase durations, below 1 decrease them."
            />
            {hints?.efficiency && <div className="-mt-2 pb-3"><SettingHint text={hints.efficiency} /></div>}
            <InfoRow
              label="Default Quote Expiration"
              value={formatValidityDays(savedValidityDays ?? null)}
              subtitle="How long quotes remain valid before expiring. Can be overridden per quote."
            />
            {hints?.validity && <div className="-mt-2 pb-3"><SettingHint text={hints.validity} /></div>}
          </>
        )}
      </div>
    </section>
  );
}

// ── Shift Settings Card (per move type) ───────────────────────────────────

/** Returns a copy of the shift list with exactly one entry marked default. */
function normalizeShiftDefault(list: ShiftSetting[]): ShiftSetting[] {
  if (list.length === 0) return [];
  const firstDefault = list.findIndex(s => s.isDefault);
  const idx = firstDefault === -1 ? 0 : firstDefault;
  return list.map((s, i) => ({ ...s, isDefault: i === idx }));
}

export function ShiftSettingsCard({ type, org, onSave, onRestore, onEditingChange, hint }: {
  type: 'commercial' | 'residential';
  org: Organization;
  onSave: (data: OrgSettingsUpdate) => Promise<void>;
  onRestore?: () => Promise<void>;
  /** Fires when the card enters or leaves edit mode (the setup wizard warns before discarding an open edit). */
  onEditingChange?: (editing: boolean) => void;
  /** "What's this?" disclosure under the header (setup wizard). */
  hint?: string;
}) {
  const savedShifts: ShiftSetting[] = normalizeShiftDefault(
    type === 'commercial'
      ? (org.commercialShiftSettings ?? DEFAULT_SHIFT_SETTINGS)
      : (org.residentialShiftSettings ?? DEFAULT_SHIFT_SETTINGS),
  );

  const [editing, setEditingState] = useState(false);
  const setEditing = (next: boolean) => { setEditingState(next); onEditingChange?.(next); };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shifts, setShifts] = useState<ShiftSetting[]>(savedShifts);

  function startEdit() {
    const current = type === 'commercial'
      ? (org.commercialShiftSettings ?? DEFAULT_SHIFT_SETTINGS)
      : (org.residentialShiftSettings ?? DEFAULT_SHIFT_SETTINGS);
    setShifts(normalizeShiftDefault(current.map(s => ({ ...s }))));
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  function addShift() {
    setShifts(prev => {
      const next = [...prev, { name: '', lengthHours: 8 }];
      return normalizeShiftDefault(next);
    });
  }

  function removeShift(index: number) {
    setShifts(prev => {
      const next = prev.filter((_, i) => i !== index);
      // If the removed entry was the default, the first remaining shift
      // becomes the new default.
      return normalizeShiftDefault(next);
    });
  }

  function updateShift(index: number, field: 'name' | 'lengthHours', value: string | number) {
    setShifts(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  }

  function setDefaultShift(index: number) {
    setShifts(prev => prev.map((s, i) => ({ ...s, isDefault: i === index })));
  }

  async function handleSave() {
    // Validate
    const valid = shifts.filter(s => s.name.trim().length > 0 && s.lengthHours > 0);
    if (valid.length === 0) {
      setError('At least one shift is required.');
      return;
    }
    const normalized = normalizeShiftDefault(valid);
    setSaving(true);
    setError('');
    try {
      const payload = normalized.map(s => ({
        name: s.name.trim(),
        lengthHours: s.lengthHours,
        ...(s.isDefault ? { isDefault: true } : {}),
      }));
      const data: OrgSettingsUpdate = type === 'commercial'
        ? { commercialShiftSettings: payload }
        : { residentialShiftSettings: payload };
      await onSave(data);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Shift Settings</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Configure the shift types available for {type} quotes. Estimators select a shift type when building a quote.
          </p>
          <SettingHint text={hint} />
        </div>
        <div className="flex items-center gap-2">
          {onRestore && !editing && <RestoreButton onRestore={onRestore} label="shift settings" />}
          <CardEditButton editing={editing} onEdit={startEdit} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
        </div>
      </div>

      <div className="px-4">
        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {editing ? (
          <div className="py-4 flex flex-col gap-3">
            {shifts.map((shift, i) => {
              const isDefault = !!shift.isDefault;
              return (
                <div key={i} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDefaultShift(i)}
                    disabled={isDefault}
                    aria-pressed={isDefault}
                    aria-label={isDefault
                      ? `${shift.name || 'Shift'} is the default shift`
                      : `Set ${shift.name || 'this shift'} as default`}
                    title={isDefault
                      ? 'Default shift'
                      : 'Set as default'}
                    className={`shrink-0 w-11 h-11 flex items-center justify-center rounded-lg transition-colors
                               ${isDefault
                                 ? 'text-amber-500 cursor-default'
                                 : 'text-slate hover:text-amber-500 hover:bg-amber-500/5'}`}
                  >
                    <Star
                      size={18}
                      fill={isDefault ? 'currentColor' : 'none'}
                      strokeWidth={isDefault ? 1.5 : 2}
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder="Shift name"
                      value={shift.name}
                      onChange={e => updateShift(i, 'name', e.target.value)}
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                  <div className="w-24 shrink-0">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.5"
                      min="0.5"
                      max="24"
                      placeholder="Hours"
                      value={shift.lengthHours}
                      onChange={e => updateShift(i, 'lengthHours', parseFloat(e.target.value) || 0)}
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150 text-center"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeShift(i)}
                    className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg
                               text-slate hover:text-rose hover:bg-rose/5 transition-colors"
                    aria-label={`Remove ${shift.name || 'shift'}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={addShift}
              className="flex items-center justify-center gap-1.5 h-11 w-full rounded-lg border border-dashed border-stone
                         text-small font-medium text-plum hover:bg-plum/5 transition-colors"
            >
              <Plus size={16} />
              Add Shift
            </button>
            <p className="text-[11px] text-slate/70 leading-snug">
              Tap the star to choose the default shift for new quotes. Exactly one shift is always the default.
            </p>
          </div>
        ) : (
          <div className="py-1">
            {savedShifts.length === 0 ? (
              <p className="py-3 text-[13px] text-slate">No shifts configured.</p>
            ) : (
              savedShifts.map((shift, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-3 border-b border-stone last:border-b-0 gap-3"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Star
                      size={14}
                      className={shift.isDefault ? 'text-amber-500 shrink-0' : 'text-stone shrink-0'}
                      fill={shift.isDefault ? 'currentColor' : 'none'}
                      strokeWidth={shift.isDefault ? 1.5 : 2}
                      aria-label={shift.isDefault ? 'Default shift' : undefined}
                    />
                    <span className="text-[13px] text-midnight truncate">{shift.name}</span>
                    {shift.isDefault && (
                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide
                                       text-amber-700 bg-amber-500/10">
                        Default
                      </span>
                    )}
                  </div>
                  <span className="text-[13px] font-semibold text-midnight shrink-0">{`${shift.lengthHours}h`}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Pricing Model Card (residential only) ─────────────────────────────────

export function PricingModelCard({ org, onSave, onRestore }: {
  org: Organization;
  onSave: (data: OrgSettingsUpdate) => Promise<void>;
  onRestore?: () => Promise<void>;
}) {
  const savedModel = org.residentialPricingModel ?? 'hourly_rate';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSelect(model: 'flat_rate' | 'hourly_rate') {
    if (model === savedModel) return;
    setSaving(true);
    setError('');
    try {
      await onSave({ residentialPricingModel: model });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  const options: { value: 'flat_rate' | 'hourly_rate'; label: string; description: string }[] = [
    {
      value: 'flat_rate',
      label: 'Flat Rate',
      description: 'Fixed price based on home size and confirmed add-ons. The quoted amount is what the customer pays \u2014 no reconciliation against actual hours worked.',
    },
    {
      value: 'hourly_rate',
      label: 'Hourly Rate',
      description: 'Final invoice is based on actual time worked by the crew. The quote gives customers an estimated cost; the close-out reconciles to real hours at the agreed rate.',
    },
  ];

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Pricing Model</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            How your quotes and final invoices are calculated &mdash; flat rate charges a fixed price based on home size, hourly charges based on actual time worked.
          </p>
        </div>
        {onRestore && <RestoreButton onRestore={onRestore} label="pricing model" />}
      </div>
      <div className="px-4 py-4">
        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}
        <div className="flex flex-col gap-3">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              disabled={saving}
              onClick={() => handleSelect(opt.value)}
              className={`w-full text-left p-4 rounded-lg border transition-colors duration-150
                         ${savedModel === opt.value
                           ? 'border-plum bg-plum/5'
                           : 'border-stone hover:border-plum/40'}
                         ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 h-5 w-5 rounded-full border-2 shrink-0 flex items-center justify-center
                                ${savedModel === opt.value ? 'border-plum' : 'border-stone'}`}>
                  {savedModel === opt.value && (
                    <div className="h-2.5 w-2.5 rounded-full bg-plum" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-midnight">{opt.label}</p>
                  <p className="text-[12px] text-slate leading-snug mt-0.5">{opt.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Contextual guidance */}
        <div className="mt-4 px-3 py-2.5 rounded-lg bg-mist border border-stone/50">
          {savedModel === 'flat_rate' ? (
            <p className="text-[12px] text-slate leading-snug">
              Configure your flat rates in the{' '}
              <Link to="/admin/quoting/rate-cards" className="text-plum font-medium hover:underline">
                Rate Cards
              </Link>{' '}
              page.
            </p>
          ) : (
            <p className="text-[12px] text-slate leading-snug">
              Configure your crew rates in the{' '}
              <Link to="/admin/quoting/rate-cards" className="text-plum font-medium hover:underline">
                Rate Cards
              </Link>{' '}
              page and customize your estimate building defaults in the{' '}
              <Link to="/admin/quoting/residential-rooms" className="text-plum font-medium hover:underline">
                Residential Rooms
              </Link>{' '}
              page (defaults are already in place, so this is optional).
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Itemization Card ───────────────────────────────────────────────────────

function ItemizationPreview({ preference, isFlatRate }: { preference: ItemizationPreference; isFlatRate?: boolean }) {
  const previews: Record<ItemizationPreference, { name: string; detail: string; amount: string }[]> = {
    single_line: isFlatRate
      ? [{ name: 'Standard Moving Service', detail: '1 \u00d7 $1,425.00', amount: '$1,425.00' }]
      : [{ name: 'Moving Services', detail: '$185.00/hr \u00d7 6.5 hrs', amount: '$1,202.50' }],
    crew_trucks: [
      { name: 'Supervisor (\u00d71)', detail: '$55.00/hr \u00d7 6.5 hrs', amount: '$357.50' },
      { name: 'Mover (\u00d72)', detail: '$80.00/hr \u00d7 6.5 hrs', amount: '$520.00' },
      { name: 'Truck + Driver (\u00d71)', detail: '$50.00/hr \u00d7 6.5 hrs', amount: '$325.00' },
    ],
    duration_transit: [
      { name: 'Move Inventory', detail: '$185.00/hr \u00d7 4.0 hrs', amount: '$740.00' },
      { name: 'Crew Travel Time (1-hour Fixed)', detail: '$185.00/hr \u00d7 1.0 hrs', amount: '$185.00' },
      { name: 'Transit Time above 1hr (est: 2.5 hrs)', detail: '$185.00/hr \u00d7 1.5 hrs', amount: '$277.50' },
    ],
    flat_rate_addons: [
      { name: 'Standard Moving Service', detail: '1 \u00d7 $1,200.00', amount: '$1,200.00' },
      { name: 'Long Carry', detail: 'Add-on', amount: '$150.00' },
      { name: 'Elevator Access', detail: 'Add-on', amount: '$75.00' },
    ],
  };

  const totals: Record<ItemizationPreference, string> = {
    single_line: isFlatRate ? '$1,425.00' : '$1,202.50',
    crew_trucks: '$1,202.50',
    duration_transit: '$1,202.50',
    flat_rate_addons: '$1,425.00',
  };

  const items = previews[preference];

  return (
    <div className="bg-mist rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-stone/50">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Preview</p>
      </div>
      <div className="px-3">
        {items.map((item, i) => (
          <div key={i} className={`py-2 ${i < items.length - 1 ? 'border-b border-stone/40' : ''}`}>
            <p className="text-[12px] font-medium text-midnight">{item.name}</p>
            <div className="flex items-center justify-between text-[11px] text-slate">
              <span>{item.detail}</span>
              <span className="font-semibold text-midnight">{item.amount}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 border-t border-stone/50 flex justify-between">
        <span className="text-[11px] font-bold text-midnight">Total</span>
        <span className="text-[12px] font-bold text-midnight">{totals[preference]}</span>
      </div>
    </div>
  );
}

function ItemizationSection({
  title,
  groupName,
  preference,
  options,
  saving,
  onChange,
  isFlatRate,
}: {
  title: string;
  groupName: string;
  preference: ItemizationPreference;
  options?: { value: ItemizationPreference; label: string; description: string }[];
  saving: boolean;
  onChange: (value: ItemizationPreference) => void;
  isFlatRate?: boolean;
}) {
  const displayOptions = options ?? ITEMIZATION_OPTIONS;
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">{title}</p>
      <div className="flex flex-col gap-2 mb-3">
        {displayOptions.map(opt => (
          <label
            key={opt.value}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors duration-150
                       ${preference === opt.value
                         ? 'border-plum bg-plum/5'
                         : 'border-stone hover:border-plum/40'}`}
          >
            <input
              type="radio"
              name={groupName}
              value={opt.value}
              checked={preference === opt.value}
              onChange={() => onChange(opt.value)}
              disabled={saving}
              className="mt-0.5 h-4 w-4 text-plum border-stone focus:ring-plum/20 shrink-0"
            />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-midnight">{opt.label}</p>
              <p className="text-[12px] text-slate leading-snug">{opt.description}</p>
            </div>
          </label>
        ))}
      </div>
      <ItemizationPreview preference={preference} isFlatRate={isFlatRate} />
    </div>
  );
}

function ToggleSwitch({ checked, onChange, disabled }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-plum/20
                  ${checked ? 'bg-plum' : 'bg-stone'}
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                    transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  );
}

export function ItemizationCard({ type, org, onSave, flatRateMode, onRestore }: {
  type: 'commercial' | 'residential';
  org: Organization;
  onSave: (data: OrgSettingsUpdate) => Promise<void>;
  flatRateMode?: boolean;
  onRestore?: () => Promise<void>;
}) {
  const savedPref = type === 'commercial'
    ? (org.commercialItemizationPreference ?? 'single_line')
    : (org.residentialItemizationPreference ?? 'single_line');
  // In flat rate mode, if the current pref isn't valid for flat rate, fall back to single_line
  const effectivePref = flatRateMode && savedPref !== 'single_line' && savedPref !== 'flat_rate_addons'
    ? 'single_line' : savedPref;
  const itemizeMaterials = type === 'commercial'
    ? (org.commercialItemizeMaterials ?? false)
    : (org.residentialItemizeMaterials ?? false);
  const itemizeScopeOfServices = type === 'commercial'
    ? (org.commercialItemizeScopeOfServices ?? false)
    : (org.residentialItemizeScopeOfServices ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleChange(value: ItemizationPreference) {
    setSaving(true);
    setError('');
    try {
      if (type === 'commercial') {
        await onSave({ commercialItemizationPreference: value });
      } else {
        await onSave({ residentialItemizationPreference: value });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  async function handleToggle(field: 'itemizeMaterials' | 'itemizeScopeOfServices', value: boolean) {
    setSaving(true);
    setError('');
    try {
      if (type === 'commercial') {
        if (field === 'itemizeMaterials') {
          await onSave({ commercialItemizeMaterials: value });
        } else {
          await onSave({ commercialItemizeScopeOfServices: value });
        }
      } else {
        if (field === 'itemizeMaterials') {
          await onSave({ residentialItemizeMaterials: value });
        } else {
          await onSave({ residentialItemizeScopeOfServices: value });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  const options = flatRateMode ? FLAT_RATE_ITEMIZATION_OPTIONS : ITEMIZATION_OPTIONS;

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Itemization Preferences</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Choose how line items appear on client-facing quote documents.
          </p>
        </div>
        {onRestore && <RestoreButton onRestore={onRestore} label="itemization settings" />}
      </div>
      <div className="px-4 py-4">
        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {/* Materials & Scope of Services — hidden in flat rate mode */}
        {!flatRateMode && (
        <div className="mb-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Materials &amp; Scope of Services</p>
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-stone">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-midnight">Itemize Materials</p>
                <p className="text-[12px] text-slate leading-snug">
                  When enabled, materials selected for a job will be listed individually on the online quote and quote PDF.
                </p>
              </div>
              <ToggleSwitch
                checked={itemizeMaterials}
                onChange={(v) => handleToggle('itemizeMaterials', v)}
                disabled={saving}
              />
            </div>
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-stone">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-midnight">Itemize Scope of Services</p>
                <p className="text-[12px] text-slate leading-snug">
                  When enabled, scope of services selected for a job will be listed individually on the online quote and quote PDF.
                </p>
              </div>
              <ToggleSwitch
                checked={itemizeScopeOfServices}
                onChange={(v) => handleToggle('itemizeScopeOfServices', v)}
                disabled={saving}
              />
            </div>
          </div>
        </div>
        )}

        <ItemizationSection
          title="Line Item Detail"
          groupName={`itemization-line-item-detail-${type}`}
          preference={effectivePref}
          options={options}
          saving={saving}
          onChange={handleChange}
          isFlatRate={flatRateMode}
        />
      </div>
    </section>
  );
}

// ── Quote Number Format Card ───────────────────────────────────────────────

function QuoteNumberFormatCard({ org, onSave }: { org: Organization; onSave: (data: OrgSettingsUpdate) => Promise<void> }) {
  const savedConfig: QuoteNumberConfig = { ...QUOTE_NUMBER_CONFIG_DEFAULTS, ...org.quoteNumberConfig };
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [qnPrefix, setQnPrefix] = useState(savedConfig.prefix);
  const [qnSeparator, setQnSeparator] = useState<QuoteNumberSeparator>(savedConfig.separator);
  const [qnDigitCount, setQnDigitCount] = useState<QuoteNumberDigitCount>(savedConfig.digitCount);
  const [qnStartingNumber, setQnStartingNumber] = useState(String(savedConfig.startingNumber));
  const [qnMethod, setQnMethod] = useState<QuoteNumberMethod>(savedConfig.method);

  function startEdit() {
    const cfg: QuoteNumberConfig = { ...QUOTE_NUMBER_CONFIG_DEFAULTS, ...org.quoteNumberConfig };
    setQnPrefix(cfg.prefix);
    setQnSeparator(cfg.separator);
    setQnDigitCount(cfg.digitCount);
    setQnStartingNumber(String(cfg.startingNumber));
    setQnMethod(cfg.method);
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await onSave({
        quoteNumberConfig: {
          prefix: qnPrefix,
          separator: qnSeparator,
          digitCount: qnDigitCount,
          startingNumber: parseInt(qnStartingNumber, 10) || 1,
          method: qnMethod,
        },
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const editPreviewConfig: QuoteNumberConfig = {
    prefix: qnPrefix,
    separator: qnSeparator,
    digitCount: qnDigitCount,
    startingNumber: parseInt(qnStartingNumber, 10) || 1,
    method: qnMethod,
  };

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Quote Number Format</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Controls how quote numbers appear on customer-facing documents.
          </p>
        </div>
        <CardEditButton editing={editing} onEdit={startEdit} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
      </div>

      <div className="px-4">
        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {/* Preview always visible */}
        <div className="bg-mist rounded-lg px-4 py-3 my-3">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Preview</p>
          <p className="font-mono text-[15px] font-semibold text-midnight">
            {quoteNumberPreview(editing ? editPreviewConfig : savedConfig)}
          </p>
        </div>

        {editing ? (
          <div className="pb-4 flex flex-col gap-4">
            {/* Prefix */}
            <div>
              <label htmlFor="qd-qn-prefix" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Prefix
              </label>
              <input
                id="qd-qn-prefix"
                type="text"
                maxLength={10}
                value={qnPrefix}
                onChange={e => setQnPrefix(e.target.value.replace(/[^a-zA-Z0-9-]/g, ''))}
                placeholder="QT"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Text that appears before the quote number
              </p>
            </div>

            {/* Separator */}
            <div>
              <label htmlFor="qd-qn-separator" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Separator
              </label>
              <CustomDropdown
                id="qd-qn-separator"
                options={SEPARATOR_OPTIONS}
                value={qnSeparator}
                onChange={v => setQnSeparator(v as QuoteNumberSeparator)}
              />
            </div>

            {/* Digit Count */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                Number of Digits
              </p>
              <SegmentedControl
                options={DIGIT_COUNT_OPTIONS}
                value={String(qnDigitCount)}
                onChange={v => setQnDigitCount(Number(v) as QuoteNumberDigitCount)}
              />
            </div>

            {/* Starting Number */}
            <div>
              <label htmlFor="qd-qn-start" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Starting Number
              </label>
              <input
                id="qd-qn-start"
                type="number"
                inputMode="numeric"
                min="1"
                value={qnStartingNumber}
                onChange={e => setQnStartingNumber(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
            </div>

            {/* Generation Method */}
            <div>
              <label htmlFor="qd-qn-method" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Generation Method
              </label>
              <CustomDropdown
                id="qd-qn-method"
                options={METHOD_OPTIONS}
                value={qnMethod}
                onChange={v => setQnMethod(v as QuoteNumberMethod)}
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                How new quote numbers are generated
              </p>
            </div>
          </div>
        ) : (
          <>
            <InfoRow
              label="Prefix"
              value={savedConfig.prefix || '(none)'}
              subtitle="Text that appears before the quote number"
            />
            <InfoRow label="Separator" value={SEPARATOR_LABELS[savedConfig.separator]} />
            <InfoRow label="Digits" value={String(savedConfig.digitCount)} />
            <InfoRow label="Starting Number" value={String(savedConfig.startingNumber)} />
            <InfoRow
              label="Generation Method"
              value={METHOD_LABELS[savedConfig.method]}
              subtitle="How new quote numbers are generated"
            />
          </>
        )}
      </div>
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function QuoteDefaultsPage() {
  const navigate = useNavigate();
  const { adminOrg, loading: profileLoading } = useUserProfile();
  const orgId = adminOrg?.orgId ?? '';
  const { org, loading, error, updateSettings } = useOrgSettings(orgId);
  const { defaults: platformDefaults } = usePlatformQuoteDefaults();

  const orgData = org as unknown as Record<string, unknown> | null;
  const commercialEnabled = isDevMode || (orgData?.featureCommercialMovingQuotes !== false);
  const residentialEnabled = isDevMode || ((orgData?.featureResidentialMovingQuotes ?? false) as boolean);
  const bothTypesEnabled = commercialEnabled && residentialEnabled;

  // Tab state — only used when both types are enabled
  const [activeTab, setActiveTab] = useState<'commercial' | 'residential'>('commercial');

  if (profileLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="flex flex-col items-center py-20 gap-3 text-center px-4">
        <p className="text-small text-slate">You don&apos;t have access to org admin settings.</p>
      </div>
    );
  }

  async function handleSave(data: OrgSettingsUpdate) {
    await updateSettings(data);
  }

  // Restore handlers — apply platform defaults to the org for each card section
  function makeRestoreQuoteBuilding(type: 'commercial' | 'residential') {
    return async () => {
      if (!platformDefaults) return;
      const data: OrgSettingsUpdate = type === 'commercial'
        ? {
            commercialDefaultTravelTimeRate: platformDefaults.commercialDefaultTravelTimeRate ?? 'one_hour_standard',
            commercialFixedTravelTimeHours: platformDefaults.commercialFixedTravelTimeHours ?? 1,
            commercialEfficiencyMultiplier: platformDefaults.commercialEfficiencyMultiplier ?? 1,
            commercialDefaultQuoteValidityDays: platformDefaults.commercialDefaultQuoteValidityDays ?? 30,
            localMoveRadiusType: platformDefaults.localMoveRadiusType ?? 'distance',
            localMoveRadiusValue: platformDefaults.localMoveRadiusValue ?? 50,
          }
        : {
            residentialDefaultTravelTimeRate: platformDefaults.residentialDefaultTravelTimeRate ?? 'one_hour_standard',
            residentialFixedTravelTimeHours: platformDefaults.residentialFixedTravelTimeHours ?? 1,
            residentialEfficiencyMultiplier: platformDefaults.residentialEfficiencyMultiplier ?? 1,
            residentialDefaultQuoteValidityDays: platformDefaults.residentialDefaultQuoteValidityDays ?? 30,
            localMoveRadiusType: platformDefaults.localMoveRadiusType ?? 'distance',
            localMoveRadiusValue: platformDefaults.localMoveRadiusValue ?? 50,
          };
      await updateSettings(data);
    };
  }

  function makeRestoreShiftSettings(type: 'commercial' | 'residential') {
    return async () => {
      if (!platformDefaults) return;
      const defaultShifts: ShiftSetting[] = [
        { name: 'Work week - day', lengthHours: 7, isDefault: true },
        { name: 'Work week - evening', lengthHours: 5 },
        { name: 'Weekend', lengthHours: 8 },
      ];
      const data: OrgSettingsUpdate = type === 'commercial'
        ? { commercialShiftSettings: platformDefaults.commercialShiftSettings ?? defaultShifts }
        : { residentialShiftSettings: platformDefaults.residentialShiftSettings ?? defaultShifts };
      await updateSettings(data);
    };
  }

  async function restoreTaxSettings() {
    if (!platformDefaults) return;
    await updateSettings({
      calculateTaxes: platformDefaults.calculateTaxes ?? false,
      defaultTaxRate: platformDefaults.defaultTaxRate ?? 0,
    });
  }

  function makeRestoreItemization(type: 'commercial' | 'residential') {
    return async () => {
      if (!platformDefaults) return;
      const data: OrgSettingsUpdate = type === 'commercial'
        ? {
            commercialItemizationPreference: platformDefaults.commercialItemizationPreference ?? 'single_line',
            commercialItemizeMaterials: platformDefaults.commercialItemizeMaterials ?? false,
            commercialItemizeScopeOfServices: platformDefaults.commercialItemizeScopeOfServices ?? false,
          }
        : {
            residentialItemizationPreference: platformDefaults.residentialItemizationPreference ?? 'single_line',
            residentialItemizeMaterials: platformDefaults.residentialItemizeMaterials ?? false,
            residentialItemizeScopeOfServices: platformDefaults.residentialItemizeScopeOfServices ?? false,
          };
      await updateSettings(data);
    };
  }

  async function restorePricingModel() {
    if (!platformDefaults) return;
    await updateSettings({
      residentialPricingModel: platformDefaults.residentialPricingModel ?? 'hourly_rate',
    });
  }

  // Determine which single type to show when only one is enabled
  const singleType: 'commercial' | 'residential' = commercialEnabled ? 'commercial' : 'residential';
  const residentialPricingModel = org?.residentialPricingModel ?? 'hourly_rate';

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl lg:max-w-4xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/quoting')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Quoting
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Quote Defaults</span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Quote Defaults</h1>
      <p className="text-[13px] text-slate mb-6">
        Travel time, efficiency, expiration, itemization, and quote number format.
      </p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load settings</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && org && (
        <div className="flex flex-col gap-4">
          {/* Tabs — only when both commercial and residential are enabled */}
          {bothTypesEnabled && (
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {(['commercial', 'residential'] as const).map(tab => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${activeTab === tab
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {tab === 'commercial' ? 'Commercial' : 'Residential'}
                </button>
              ))}
            </div>
          )}

          {/* Content — tabbed or single type */}
          {bothTypesEnabled ? (
            <>
              {/* Commercial tab content */}
              {activeTab === 'commercial' && (
                <div className="flex flex-col gap-4">
                  <QuoteBuildingCard type="commercial" org={org} onSave={handleSave} onRestore={platformDefaults ? makeRestoreQuoteBuilding('commercial') : undefined} />
                  <ShiftSettingsCard type="commercial" org={org} onSave={handleSave} onRestore={platformDefaults ? makeRestoreShiftSettings('commercial') : undefined} />
                  <CalculateTaxesCard org={org} onSave={handleSave} onRestore={platformDefaults ? restoreTaxSettings : undefined} />
                  <ItemizationCard type="commercial" org={org} onSave={handleSave} onRestore={platformDefaults ? makeRestoreItemization('commercial') : undefined} />
                </div>
              )}

              {/* Residential tab content */}
              {activeTab === 'residential' && (
                <div className="flex flex-col gap-4">
                  <QuoteBuildingCard type="residential" org={org} onSave={handleSave} onRestore={platformDefaults ? makeRestoreQuoteBuilding('residential') : undefined} />
                  <ShiftSettingsCard type="residential" org={org} onSave={handleSave} onRestore={platformDefaults ? makeRestoreShiftSettings('residential') : undefined} />
                  <PricingModelCard org={org} onSave={handleSave} onRestore={platformDefaults ? restorePricingModel : undefined} />
                  <CalculateTaxesCard org={org} onSave={handleSave} onRestore={platformDefaults ? restoreTaxSettings : undefined} />
                  <ItemizationCard type="residential" org={org} onSave={handleSave} flatRateMode={residentialPricingModel === 'flat_rate'} onRestore={platformDefaults ? makeRestoreItemization('residential') : undefined} />
                </div>
              )}
            </>
          ) : (
            /* Single type — no tabs */
            <div className="flex flex-col gap-4">
              <QuoteBuildingCard type={singleType} org={org} onSave={handleSave} onRestore={platformDefaults ? makeRestoreQuoteBuilding(singleType) : undefined} />
              <ShiftSettingsCard type={singleType} org={org} onSave={handleSave} onRestore={platformDefaults ? makeRestoreShiftSettings(singleType) : undefined} />
              {singleType === 'residential' && (
                <PricingModelCard org={org} onSave={handleSave} onRestore={platformDefaults ? restorePricingModel : undefined} />
              )}
              <CalculateTaxesCard org={org} onSave={handleSave} onRestore={platformDefaults ? restoreTaxSettings : undefined} />
              <ItemizationCard
                type={singleType}
                org={org}
                onSave={handleSave}
                flatRateMode={singleType === 'residential' && residentialPricingModel === 'flat_rate'}
                onRestore={platformDefaults ? makeRestoreItemization(singleType) : undefined}
              />
            </div>
          )}

          {/* Divider — Quote Number Format applies to all quote types */}
          {bothTypesEnabled && (
            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 border-t border-stone" />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-slate whitespace-nowrap">All Quote Types</span>
              <div className="flex-1 border-t border-stone" />
            </div>
          )}

          <QuoteNumberFormatCard org={org} onSave={handleSave} />
        </div>
      )}
    </div>
  );
}
